import React, { useEffect, useState, useRef, lazy } from 'react';
import { Filter, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Shuffle, Grid, List, Search } from 'lucide-react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
export const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'masonry'>('grid');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const dragConstraintsRef = useRef(null);
  const controls = useAnimation();
  // Gallery images organized by location and year
  const galleryCategories = [{
    location: 'Abuja',
    year: '2024',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mStosbmgsDJZaVNXWK79Vp/IMG-20241230-WA0235.jpg',
    coverAlt: 'Networking participants holding a sign at Abuja 2024 salon',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mStosbmgsDJZaVNXWK79Vp/IMG-20241230-WA0235.jpg',
      alt: 'Two participants holding "With Networking Magic Happens" sign at Abuja 2024 salon',
      tags: ['sign', 'duo', 'networking']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ap4bmaPe9hmEptZ6WsRMrf/IMG-20241230-WA0241.jpg',
      alt: 'Two men holding "Forging Lasting Connections" sign at Abuja 2024 event',
      tags: ['sign', 'duo', 'connections']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iDwtrQyjqxo85W3ty8cyfb/IMG-20241230-WA0255.jpg',
      alt: 'Two participants with "Collaborate And Grow" sign at Abuja 2024 networking salon',
      tags: ['sign', 'duo', 'collaboration']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xnN9ZhpkVeKWdkMVpHza9P/IMG-20241230-WA0257.jpg',
      alt: 'Woman and man holding "Build Bridges Here" sign at Abuja 2024 networking event',
      tags: ['sign', 'duo', 'bridges']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ukzdn4cZkNVnrwX98CEf3t/IMG-20241230-WA0285.jpg',
      alt: 'Two participants with "Build Bridges Here" sign at Abuja 2024 salon',
      tags: ['sign', 'duo', 'bridges']
    }]
  }, {
    location: 'Benin',
    year: 'January 2024',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fUrvThWdJVSVsYbHQSSyQ8/SAV_8518.jpg',
    coverAlt: 'Group photo at The Networking Salon Benin January 2024',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sPMQ7cKtgN3BLeiHF9y8Kn/SAV_8260.jpg',
      alt: 'Participants shaking hands at Benin January 2024 networking event',
      tags: ['handshake', 'greeting', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vqYAu12xeCB8m1eJ25tfZh/SAV_8332.jpg',
      alt: 'Presenter in light blue attire at Benin January 2024 salon',
      tags: ['presenter', 'speaking', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pKFWp4VC5e3Pwz9v6vhjBR/SAV_8448.jpg',
      alt: 'Three participants with collaborative sign at Benin January 2024 salon',
      tags: ['group', 'sign', 'collaboration']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fUrvThWdJVSVsYbHQSSyQ8/SAV_8518.jpg',
      alt: 'Group photo of participants at the Benin January 2024 networking salon',
      tags: ['group', 'photo', 'large']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1gwke6zSyiBgy5sWBe6ogs/SAV_8246.jpg',
      alt: 'Networking conversation at Benin January 2024 salon',
      tags: ['conversation', 'networking', 'informal']
    }]
  }, {
    location: 'Benin',
    year: '2024',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1BbFm8kmubumv56AGcAZxq/1755202597061.jpg',
    coverAlt: 'The Networking Salon event in Benin 2024',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1BbFm8kmubumv56AGcAZxq/1755202597061.jpg',
      alt: 'The Networking Salon event in Benin 2024',
      tags: ['event', 'salon', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aftEvNay6iufkLwXy7PyxK/1755202597036.jpg',
      alt: 'Networking event participants in Benin 2024',
      tags: ['participants', 'networking', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tiV51YTW2Kgksy4S6DCFzv/9E5A0115.jpg',
      alt: 'Speaker in traditional blue attire at Benin 2024 networking salon',
      tags: ['speaker', 'traditional', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jdYASKwaTWbg8Zeg4zbzfL/9E5A0116.jpg',
      alt: 'Group of attendees at Benin 2024 salon listening to presentation',
      tags: ['group', 'listening', 'presentation']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1o2JxTTB5rd1HeQtR4QeD5/SAV_8246.jpg',
      alt: 'Networking conversation at Benin 2024 salon',
      tags: ['conversation', 'networking', 'informal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e4tPqZYswWdLwYmJt2uJ4H/SAV_8260.jpg',
      alt: 'Participants shaking hands at Benin 2024 networking event',
      tags: ['handshake', 'greeting', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nYwfv4bFC8ShRoMKyHwPW9/SAV_8309.jpg',
      alt: 'Speaker sharing ideas at Benin 2024 salon',
      tags: ['speaker', 'sharing', 'ideas']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ieFEvEtnDSK8Bs5n6SjPUf/SAV_8332.jpg',
      alt: 'Presenter in light blue attire at Benin 2024 salon',
      tags: ['presenter', 'formal', 'speaking']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tJLvYiUA6HseBucNGeHbTB/SAV_8448.jpg',
      alt: 'Three participants with collaborative sign at Benin 2024 salon',
      tags: ['group', 'sign', 'collaboration']
    }]
  }, {
    location: 'Benin',
    year: '2022',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/252LSHK84ggBH7aCLZhcgc/a24.jpg',
    coverAlt: 'Networking event in Benin 2022',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/252LSHK84ggBH7aCLZhcgc/a24.jpg',
      alt: 'Networking event in Benin 2022 - Participants having a meal',
      tags: ['meal', 'dining', 'informal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dRteZgab5q3tv2y2YjKb1E/a27.jpg',
      alt: 'Networking event group photo in Benin 2022',
      tags: ['group', 'photo', 'large']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cxb3FJEMV3mP9TXwBXyfRv/a18.jpg',
      alt: 'Participant speaking at Benin 2022 salon',
      tags: ['speaker', 'participant', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aW7ysQsfvZg1F186uWgoYf/a14.jpg',
      alt: 'Speaker with microphone at Benin 2022 salon',
      tags: ['speaker', 'microphone', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dkQDRJ84X5BmuvWQ7w3CHQ/a9.jpg',
      alt: 'Participant sharing ideas at Benin 2022 salon',
      tags: ['participant', 'sharing', 'ideas']
    }]
  }, {
    location: 'Abuja',
    year: '2022',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hqCpx22Yii18MU7bJ8Fsfi/IMG-20220824-WA0124.jpg',
    coverAlt: 'Evening networking event in Abuja 2022',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hqCpx22Yii18MU7bJ8Fsfi/IMG-20220824-WA0124.jpg',
      alt: 'Evening networking event with yellow lanterns in Abuja 2022',
      tags: ['evening', 'lanterns', 'ambiance']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hXxv7SzVH7sPyDUKohjgSX/IMG-20220824-WA0152.jpg',
      alt: 'Woman in orange outfit at Abuja 2022 networking salon',
      tags: ['woman', 'orange', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gpSxYfExm72etV9WcMUv11/IMG-20220824-WA0156.jpg',
      alt: 'Man speaking at networking event in Abuja 2022',
      tags: ['man', 'speaking', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jwHMdt32zhXEViJa4g921W/IMG-20220824-WA0087.jpg',
      alt: 'Man in suit engaging with attendees at Abuja 2022 salon',
      tags: ['man', 'suit', 'engaging']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pGv9Amrjjy2zTAP2t23RNP/IMG-20220824-WA0081.jpg',
      alt: 'Group discussion at outdoor venue in Abuja 2022 salon',
      tags: ['group', 'discussion', 'outdoor']
    }]
  }, {
    location: 'Lagos',
    year: '2025',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gLvZ6fTjQSpPs86zUNEWZz/Group.jpg',
    coverAlt: 'The Networking Salon group photo from Lagos 2025',
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9ciCFjhcNKhhvWPuNF4wxL/Network_Scoot.jpg',
      alt: 'Networking event with people in conversation in Lagos 2025',
      tags: ['networking', 'conversation', 'informal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gumJfCcrUgAhRz8naofitq/Network_tobi.jpg',
      alt: 'Networking discussion between participants in Lagos 2025',
      tags: ['discussion', 'participants', 'informal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/62DL8VzUWoZSsys88qcCmf/speaking_anne.jpg',
      alt: 'Speaker presenting at a networking event in Lagos 2025',
      tags: ['speaker', 'presenting', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gLvZ6fTjQSpPs86zUNEWZz/Group.jpg',
      alt: 'The Networking Salon group photo from Lagos 2025',
      tags: ['group', 'photo', 'large']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uiXQQAf7xCj7Df9afZdc12/five_ladies.jpg',
      alt: 'Five women at The Networking Salon event in Lagos 2025',
      tags: ['women', 'group', 'formal']
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kuYFBEnx6pGWYjXhvRTywy/Four_uke.jpg',
      alt: 'Four people at The Networking Salon event in Lagos 2025',
      tags: ['group', 'four', 'formal']
    }]
  }];
  // Get unique locations and years for filters
  const locations = [...new Set(galleryCategories.map(cat => cat.location))];
  const years = [...new Set(galleryCategories.map(cat => cat.year))];
  // Filter the gallery based on active filters and search query
  const filteredCategories = galleryCategories.filter(category => {
    if (activeFilter && category.location !== activeFilter) return false;
    if (activeYear && category.year !== activeYear) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesLocation = category.location.toLowerCase().includes(query);
      const matchesYear = category.year.toLowerCase().includes(query);
      const matchesAlt = category.images.some(img => img.alt.toLowerCase().includes(query));
      const matchesTags = category.images.some(img => img.tags && img.tags.some(tag => tag.toLowerCase().includes(query)));
      return matchesLocation || matchesYear || matchesAlt || matchesTags;
    }
    return true;
  });
  // Reset filters
  const resetFilters = () => {
    setActiveFilter(null);
    setActiveYear(null);
    setSearchQuery('');
    setIsSearchOpen(false);
  };
  // Get active category images
  const getActiveCategoryImages = () => {
    if (!activeCategory) return [];
    const [location, year] = activeCategory.split('-');
    const category = galleryCategories.find(cat => cat.location === location && cat.year === year);
    return category ? category.images : [];
  };
  // Handle lightbox navigation
  const nextImage = () => {
    const images = getActiveCategoryImages();
    setActiveImageIndex(prev => (prev + 1) % images.length);
  };
  const prevImage = () => {
    const images = getActiveCategoryImages();
    setActiveImageIndex(prev => (prev - 1 + images.length) % images.length);
  };
  // Close lightbox when escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
        setZoomLevel(1);
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === '+' || e.key === '=') {
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
      } else if (e.key === '-') {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
      } else if (e.key === '0') {
        setZoomLevel(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIndex]);
  // Open lightbox
  const openLightbox = (categoryId: string, imageIndex: number = 0) => {
    setActiveCategory(categoryId);
    setActiveImageIndex(imageIndex);
    setIsLightboxOpen(true);
    setZoomLevel(1);
  };
  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'grid' ? 'masonry' : 'grid');
  };
  // Handle zoom controls
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  const handleZoomReset = () => {
    setZoomLevel(1);
  };
  // Shuffle the images
  const shuffleImages = async () => {
    if (isShuffling) return;
    setIsShuffling(true);
    // Animate out
    await controls.start({
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3
      }
    });
    // Wait a bit
    await new Promise(resolve => setTimeout(resolve, 300));
    // Animate back in
    await controls.start({
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        type: 'spring',
        stiffness: 200,
        damping: 20
      }
    });
    setIsShuffling(false);
  };
  const handleSearchToggle = () => {
    setIsSearchOpen(prev => !prev);
    if (isSearchOpen) {
      setSearchQuery('');
    }
  };
  return <div className="pt-20 bg-white" data-id="element-2944">
      <section className="py-16 bg-gray-50" data-id="element-2945">
        <div className="container mx-auto px-4" data-id="element-2946">
          <h1 className="text-4xl font-bold mb-6 text-center text-gray-800" data-id="element-2947">
            Event Gallery
          </h1>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-2948">
            Browse photos from our networking salons across different locations
            and years.
          </p>
          {/* Controls */}
          <div className="mb-6 flex flex-wrap justify-center gap-4" data-id="element-2949">
            <motion.button whileTap={{
            scale: 0.95
          }} className="px-4 py-2 border-2 border-gray-300 rounded-md flex items-center gap-2 hover:border-gray-800 transition-colors" onClick={toggleViewMode} data-id="element-2950">
              {viewMode === 'grid' ? <List size={18} data-id="element-2951" /> : <Grid size={18} data-id="element-2952" />}
              <span data-id="element-2953">{viewMode === 'grid' ? 'Masonry View' : 'Grid View'}</span>
            </motion.button>
            <motion.button whileTap={{
            scale: 0.95
          }} className="px-4 py-2 border-2 border-gray-300 rounded-md flex items-center gap-2 hover:border-gray-800 transition-colors" onClick={shuffleImages} disabled={isShuffling} data-id="element-2954">
              <Shuffle size={18} data-id="element-2955" />
              <span data-id="element-2956">Shuffle</span>
            </motion.button>
            <motion.button whileTap={{
            scale: 0.95
          }} className="px-4 py-2 border-2 border-gray-300 rounded-md flex items-center gap-2 hover:border-gray-800 transition-colors" onClick={handleSearchToggle} data-id="element-2957">
              <Search size={18} data-id="element-2958" />
              <span data-id="element-2959">{isSearchOpen ? 'Close Search' : 'Search'}</span>
            </motion.button>
          </div>
          {/* Search Bar */}
          <AnimatePresence data-id="element-2960">
            {isSearchOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="overflow-hidden mb-8" data-id="element-2961">
                <div className="max-w-xl mx-auto" data-id="element-2962">
                  <div className="relative" data-id="element-2963">
                    <input type="text" placeholder="Search by location, year, or content..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-800 pr-10" data-id="element-2964" />
                    {searchQuery && <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800" onClick={() => setSearchQuery('')} data-id="element-2965">
                        <X size={18} data-id="element-2966" />
                      </button>}
                  </div>
                  {searchQuery && <p className="mt-2 text-sm text-gray-600" data-id="element-2967">
                      Found {filteredCategories.length} categories matching your
                      search
                    </p>}
                </div>
              </motion.div>}
          </AnimatePresence>
          {/* Filters */}
          <div className="mb-12 max-w-4xl mx-auto" data-id="element-2968">
            <div className="flex items-center justify-center mb-4" data-id="element-2969">
              <Filter size={20} className="mr-2 text-gray-600" data-id="element-2970" />
              <h2 className="text-xl font-bold text-gray-800" data-id="element-2971">
                Filter Gallery
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2972">
              <div data-id="element-2973">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2974">
                  Location
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-2975">
                  {locations.map(location => <motion.button key={location} onClick={() => setActiveFilter(activeFilter === location ? null : location)} className={`px-3 py-1 border-2 ${activeFilter === location ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300 hover:border-gray-400'} rounded-full text-sm transition-colors`} whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} data-id="element-2976">
                      {location}
                    </motion.button>)}
                </div>
              </div>
              <div data-id="element-2977">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2978">
                  Year/Period
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-2979">
                  {years.map(year => <motion.button key={year} onClick={() => setActiveYear(activeYear === year ? null : year)} className={`px-3 py-1 border-2 ${activeYear === year ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300 hover:border-gray-400'} rounded-full text-sm transition-colors`} whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} data-id="element-2980">
                      {year}
                    </motion.button>)}
                </div>
              </div>
            </div>
            {(activeFilter || activeYear || searchQuery) && <div className="text-center mt-4" data-id="element-2981">
                <motion.button onClick={resetFilters} className="text-sm text-gray-600 hover:text-gray-800 underline" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-2982">
                  Clear all filters
                </motion.button>
              </div>}
          </div>
        </div>
      </section>
      {/* Interactive Gallery Grid */}
      {filteredCategories.length > 0 ? <section className="py-12" data-id="element-2983">
          <div className="container mx-auto px-4" data-id="element-2984">
            <motion.div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6'}`} animate={controls} data-id="element-2985">
              {filteredCategories.map(category => <motion.div key={`${category.location}-${category.year}`} className={`relative group ${viewMode === 'masonry' ? 'mb-6 break-inside-avoid' : ''} border-2 border-dashed border-gray-300 rounded-lg overflow-hidden`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} whileHover={{
            scale: 1.02,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            borderStyle: 'solid'
          }} layout data-id="element-2986">
                  <div className="aspect-[4/3] relative" data-id="element-2987">
                    <img src={category.coverImage} alt={category.coverAlt} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300" loading="lazy" style={{
                maxWidth: '100%',
                height: 'auto'
              }} data-id="element-2988" />
                    <motion.div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" initial={{
                opacity: 0
              }} whileHover={{
                opacity: 1
              }} data-id="element-2989">
                      <div className="text-white text-center p-4" data-id="element-2990">
                        <p className="text-xl font-bold" data-id="element-2991">
                          {category.location}, {category.year}
                        </p>
                        <p className="text-sm mt-2" data-id="element-2992">
                          {category.images.length} photos
                        </p>
                        <motion.button onClick={() => openLightbox(`${category.location}-${category.year}`)} className="mt-4 px-4 py-2 border border-white rounded-md hover:bg-white hover:text-black transition-colors" whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} data-id="element-2993">
                          View All
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                  <motion.div className="absolute inset-0 bg-gray-900 opacity-0 group-hover:opacity-95 transition-opacity duration-300 overflow-hidden" initial={{
              opacity: 0
            }} whileHover={{
              opacity: 0.95
            }} data-id="element-2994">
                    <div className="absolute top-4 left-4 z-10" data-id="element-2995">
                      <h3 className="text-white text-xl font-bold" data-id="element-2996">
                        {category.location}, {category.year}
                      </h3>
                      <p className="text-gray-300 text-sm" data-id="element-2997">
                        {category.images.length} photos
                      </p>
                    </div>
                    <div className="absolute inset-0 pt-16 px-4 pb-4 grid grid-cols-3 gap-2 overflow-y-auto" data-id="element-2998">
                      {category.images.map((image, index) => <motion.div key={index} className="aspect-square relative cursor-pointer border border-gray-600 hover:border-white transition-colors" onClick={() => openLightbox(`${category.location}-${category.year}`, index)} whileHover={{
                  scale: 1.05,
                  zIndex: 1
                }} whileTap={{
                  scale: 0.95
                }} data-id="element-2999">
                          <img src={image.src} alt={image.alt} className="w-full h-full object-cover" loading="lazy" style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }} data-id="element-3000" />
                        </motion.div>)}
                    </div>
                  </motion.div>
                  {/* Category Label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-75 text-white p-3" data-id="element-3001">
                    <p className="font-bold" data-id="element-3002">
                      {category.location}, {category.year}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </section> : <section className="py-16" data-id="element-3003">
          <div className="container mx-auto px-4 text-center" data-id="element-3004">
            <motion.div className="border-2 border-dashed border-gray-300 rounded-lg p-12" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3005">
              <p className="text-gray-600 mb-4" data-id="element-3006">
                No images match your current filters.
              </p>
              <motion.button onClick={resetFilters} className="px-4 py-2 border border-gray-400 rounded-md hover:bg-gray-100" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-3007">
                Reset Filters
              </motion.button>
            </motion.div>
          </div>
        </section>}
      {/* Lightbox */}
      <AnimatePresence data-id="element-3008">
        {isLightboxOpen && <motion.div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-3009">
            <motion.button className="absolute top-4 right-4 text-white p-2 hover:bg-gray-800 rounded-full transition-colors z-50" onClick={() => setIsLightboxOpen(false)} whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} data-id="element-3010">
              <X size={24} data-id="element-3011" />
            </motion.button>
            <motion.button className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-2 hover:bg-gray-800 rounded-full transition-colors z-50" onClick={prevImage} whileHover={{
          scale: 1.1,
          x: -5
        }} whileTap={{
          scale: 0.9
        }} data-id="element-3012">
              <ChevronLeft size={24} data-id="element-3013" />
            </motion.button>
            <div ref={dragConstraintsRef} className="w-full h-full flex items-center justify-center overflow-hidden" data-id="element-3014">
              {activeCategory && <div className="relative max-w-5xl max-h-[80vh] flex flex-col" data-id="element-3015">
                  <div className="flex-grow overflow-hidden" data-id="element-3016">
                    <motion.div drag dragConstraints={dragConstraintsRef} dragElastic={0.1} className="cursor-grab active:cursor-grabbing" style={{
                touchAction: 'none'
              }} data-id="element-3017">
                      <motion.img src={getActiveCategoryImages()[activeImageIndex]?.src} alt={getActiveCategoryImages()[activeImageIndex]?.alt} className="max-w-full max-h-[65vh] object-contain" style={{
                  scale: zoomLevel
                }} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: zoomLevel
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} key={`${activeCategory}-${activeImageIndex}`} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }} data-id="element-3018" />
                    </motion.div>
                  </div>
                  {/* Image Info and Controls */}
                  <div className="bg-black bg-opacity-75 text-white p-4" data-id="element-3019">
                    <div className="flex justify-between items-center mb-2" data-id="element-3020">
                      <p className="text-sm" data-id="element-3021">
                        {activeImageIndex + 1} /{' '}
                        {getActiveCategoryImages().length}
                      </p>
                      <div className="flex items-center gap-2" data-id="element-3022">
                        <motion.button onClick={handleZoomOut} className="p-1 hover:bg-gray-700 rounded-full" whileHover={{
                    scale: 1.1
                  }} whileTap={{
                    scale: 0.9
                  }} disabled={zoomLevel <= 0.5} data-id="element-3023">
                          <ZoomOut size={18} data-id="element-3024" />
                        </motion.button>
                        <motion.button onClick={handleZoomReset} className="p-1 hover:bg-gray-700 rounded-full" whileHover={{
                    scale: 1.1
                  }} whileTap={{
                    scale: 0.9
                  }} data-id="element-3025">
                          <span className="text-xs px-1" data-id="element-3026">
                            {Math.round(zoomLevel * 100)}%
                          </span>
                        </motion.button>
                        <motion.button onClick={handleZoomIn} className="p-1 hover:bg-gray-700 rounded-full" whileHover={{
                    scale: 1.1
                  }} whileTap={{
                    scale: 0.9
                  }} disabled={zoomLevel >= 3} data-id="element-3027">
                          <ZoomIn size={18} data-id="element-3028" />
                        </motion.button>
                      </div>
                    </div>
                    <p className="text-sm" data-id="element-3029">
                      {getActiveCategoryImages()[activeImageIndex]?.alt}
                    </p>
                    {/* Tags */}
                    {getActiveCategoryImages()[activeImageIndex]?.tags && <div className="flex flex-wrap gap-1 mt-2" data-id="element-3030">
                        {getActiveCategoryImages()[activeImageIndex]?.tags?.map((tag, index) => <span key={index} className="text-xs bg-gray-700 px-2 py-0.5 rounded-full" data-id="element-3031">
                              {tag}
                            </span>)}
                      </div>}
                  </div>
                </div>}
            </div>
            <motion.button className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white p-2 hover:bg-gray-800 rounded-full transition-colors z-50" onClick={nextImage} whileHover={{
          scale: 1.1,
          x: 5
        }} whileTap={{
          scale: 0.9
        }} data-id="element-3032">
              <ChevronRight size={24} data-id="element-3033" />
            </motion.button>
            {/* Thumbnail Navigation */}
            <div className="absolute bottom-4 left-4 right-4 overflow-x-auto" data-id="element-3034">
              <div className="flex space-x-2 justify-center" data-id="element-3035">
                {getActiveCategoryImages().map((image, index) => <motion.button key={index} className={`w-14 h-10 flex-shrink-0 border-2 ${index === activeImageIndex ? 'border-white' : 'border-gray-600'}`} onClick={() => setActiveImageIndex(index)} whileHover={{
              scale: 1.1,
              y: -2
            }} whileTap={{
              scale: 0.95
            }} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay: 0.1 * index % 5
              }
            }} data-id="element-3036">
                    <img src={image.src} alt="thumbnail" className="w-full h-full object-cover" data-id="element-3037" />
                  </motion.button>)}
              </div>
            </div>
            {/* Keyboard shortcut hints */}
            <div className="absolute top-4 left-4 text-white text-xs" data-id="element-3038">
              <p data-id="element-3039">
                ← → arrows: navigate • + - keys: zoom • 0: reset zoom • ESC:
                close
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
      {/* Keyboard shortcuts info */}
      <div className="container mx-auto px-4 py-8" data-id="element-3040">
        <div className="border-t border-gray-200 pt-8" data-id="element-3041">
          <h2 className="text-xl font-bold text-gray-700 mb-4" data-id="element-3042">
            Gallery Keyboard Shortcuts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3043">
            <div className="border border-gray-300 rounded-md p-4" data-id="element-3044">
              <h3 className="font-medium mb-2" data-id="element-3045">Navigation</h3>
              <ul className="space-y-1 text-sm text-gray-600" data-id="element-3046">
                <li className="flex items-center gap-2" data-id="element-3047">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3048">
                    ←
                  </kbd>
                  <span data-id="element-3049">Previous image</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-3050">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3051">
                    →
                  </kbd>
                  <span data-id="element-3052">Next image</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-3053">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3054">
                    Esc
                  </kbd>
                  <span data-id="element-3055">Close lightbox</span>
                </li>
              </ul>
            </div>
            <div className="border border-gray-300 rounded-md p-4" data-id="element-3056">
              <h3 className="font-medium mb-2" data-id="element-3057">Zoom Controls</h3>
              <ul className="space-y-1 text-sm text-gray-600" data-id="element-3058">
                <li className="flex items-center gap-2" data-id="element-3059">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3060">
                    +
                  </kbd>
                  <span data-id="element-3061">Zoom in</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-3062">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3063">
                    -
                  </kbd>
                  <span data-id="element-3064">Zoom out</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-3065">
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md" data-id="element-3066">
                    0
                  </kbd>
                  <span data-id="element-3067">Reset zoom</span>
                </li>
              </ul>
            </div>
            <div className="border border-gray-300 rounded-md p-4" data-id="element-3068">
              <h3 className="font-medium mb-2" data-id="element-3069">Interaction Tips</h3>
              <ul className="space-y-1 text-sm text-gray-600" data-id="element-3070">
                <li className="flex items-center gap-2" data-id="element-3071">
                  <span className="w-4 h-4 flex items-center justify-center" data-id="element-3072">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-3073">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" data-id="element-3074"></path>
                    </svg>
                  </span>
                  <span data-id="element-3075">Drag to pan when zoomed</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-3076">
                  <span className="w-4 h-4 flex items-center justify-center" data-id="element-3077">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-3078">
                      <circle cx="12" cy="12" r="10" data-id="element-3079"></circle>
                      <line x1="12" y1="8" x2="12" y2="16" data-id="element-3080"></line>
                      <line x1="8" y1="12" x2="16" y2="12" data-id="element-3081"></line>
                    </svg>
                  </span>
                  <span data-id="element-3082">Click thumbnail to jump to image</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
};